/**
 * Kbd — keyboard shortcut chips.
 *
 * Pass logical keys (`['mod','k']`); the component resolves `mod` to ⌘ on Apple
 * platforms and Ctrl everywhere else, and gives assistive tech a spoken form
 * ("Command K") rather than a string of glyphs.
 */

import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const IS_APPLE =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);

interface KeyGlyph {
  glyph: string;
  spoken: string;
}

function resolveKey(key: string): KeyGlyph {
  switch (key.toLowerCase()) {
    case 'mod':
      return IS_APPLE ? { glyph: '⌘', spoken: 'Command' } : { glyph: 'Ctrl', spoken: 'Control' };
    case 'cmd':
    case 'meta':
    case 'command':
      return { glyph: '⌘', spoken: 'Command' };
    case 'ctrl':
    case 'control':
      return IS_APPLE ? { glyph: '⌃', spoken: 'Control' } : { glyph: 'Ctrl', spoken: 'Control' };
    case 'alt':
    case 'option':
      return IS_APPLE ? { glyph: '⌥', spoken: 'Option' } : { glyph: 'Alt', spoken: 'Alt' };
    case 'shift':
      return { glyph: '⇧', spoken: 'Shift' };
    case 'enter':
    case 'return':
      return { glyph: '↵', spoken: 'Enter' };
    case 'esc':
    case 'escape':
      return { glyph: 'Esc', spoken: 'Escape' };
    case 'backspace':
      return { glyph: '⌫', spoken: 'Backspace' };
    case 'delete':
      return { glyph: '⌦', spoken: 'Delete' };
    case 'tab':
      return { glyph: '⇥', spoken: 'Tab' };
    case 'space':
      return { glyph: 'Space', spoken: 'Space' };
    case 'up':
    case 'arrowup':
      return { glyph: '↑', spoken: 'Arrow up' };
    case 'down':
    case 'arrowdown':
      return { glyph: '↓', spoken: 'Arrow down' };
    case 'left':
    case 'arrowleft':
      return { glyph: '←', spoken: 'Arrow left' };
    case 'right':
    case 'arrowright':
      return { glyph: '→', spoken: 'Arrow right' };
    default:
      return { glyph: key.length === 1 ? key.toUpperCase() : key, spoken: key.toUpperCase() };
  }
}

export interface KbdProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  keys: string[];
  size?: 'sm' | 'md';
  /** `then` renders the keys as a sequence (`G` then `I`) instead of a chord. */
  variant?: 'chord' | 'then';
}

export function Kbd({ keys, size = 'md', variant = 'chord', className, ...rest }: KbdProps) {
  const resolved = keys.map(resolveKey);
  const spoken = resolved
    .map((key) => key.spoken)
    .join(variant === 'then' ? ' then ' : ' ');

  return (
    <span
      role="img"
      aria-label={spoken}
      className={cn('inline-flex shrink-0 items-center gap-0.5 align-middle', className)}
      {...rest}
    >
      {resolved.map((key, index) => (
        <span key={`${key.glyph}-${index}`} className="inline-flex items-center gap-0.5">
          {variant === 'then' && index > 0 ? (
            <span aria-hidden="true" className="px-0.5 text-[length:var(--text-2xs)] text-muted">
              then
            </span>
          ) : null}
          <kbd
            aria-hidden="true"
            className={cn(
              'inline-grid place-items-center rounded-xs border border-line bg-surface-3',
              // --text-muted on --surface-3 measured 4.32:1 in dark: a chip fill
              // is a lighter ground than the row it sits on, so the label has to
              // come up with it.
              'font-mono font-medium text-secondary',
              // The line box is named, not inherited: `text-xs`/`text-2xs` carry
              // Tailwind's own 1.33 ratio, which lands on 14.67px and 13.33px and
              // drags a fractional line-height into every region that shows a chip.
              size === 'sm'
                ? 'h-4 min-w-4 px-1 text-[length:var(--text-2xs)] leading-[16px]'
                : 'h-[18px] min-w-[18px] px-1 text-xs leading-[16px]',
            )}
          >
            {key.glyph}
          </kbd>
        </span>
      ))}
    </span>
  );
}
